// src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import ApiService from '../services/ApiService';

// Créer le contexte
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

// Provider du contexte
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Vérifier le statut d'authentification
  const checkAuthStatus = async () => {
    setLoading(true);
    
    try {
      // Vérifier d'abord le localStorage
      const storedUser = ApiService.getCurrentUser();
      const token = localStorage.getItem('authToken');
      
      if (token && storedUser) {
        // Vérifier la validité auprès du serveur
        try {
          const response = await ApiService.checkAuth();
          if (response.authenticated) {
            setUser(response.user);
            setIsAuthenticated(true);
          } else {
            // Token invalide, nettoyer
            logout();
          }
        } catch (error) {
          // En cas d'erreur réseau, garder l'utilisateur connecté localement
          setUser(storedUser);
          setIsAuthenticated(true);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      // console.error('Erreur lors de la vérification de l\'authentification:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Inscription
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.register(userData);
      
      if (response.success) {
        // Stocker les informations
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        setUser(response.user);
        setIsAuthenticated(true);
        
        return { success: true, user: response.user };
      }
      
      // Vérifier si c'est le cas spécial du numéro déjà utilisé
      if (response.shouldLogin) {
        // console.log('AuthContext: Numéro déjà utilisé, redirige vers connexion', response);
        return {
          success: false,
          message: response.message,
          shouldLogin: response.shouldLogin,
          telephone: response.telephone
        };
      }
      
      // Pour tous les autres cas d'erreur, garder le comportement original
      return { success: false, message: response.message };
      
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Connexion - Étape 1
  const login = async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.login({ email });
      return { success: response.success, message: response.message };
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Connexion par téléphone - Étape 1
  const loginPhone = async (telephone) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.loginPhone(telephone);
      return { success: response.success, message: response.message };
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Modifier la méthode verifyOTP existante pour accepter email ou téléphone
  const verifyOTP = async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.verifyOTP(data);
      
      if (response.success) {
        // Stocker les informations
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        setUser(response.user);
        setIsAuthenticated(true);
        
        return { success: true, user: response.user };
      }
      
      return { success: false, message: response.message };
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le numéro de téléphone (nouvelle méthode)
  const verifyPhoneNumber = async (telephone) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.verifyPhone(telephone);
      return { success: response.success, message: response.message };
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Vérifier l'adresse email (nouvelle méthode)
  const verifyEmailAddress = async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.verifyEmailAddress(email);
      return { success: response.success, message: response.message };
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Déconnexion
  const logout = async () => {
    setLoading(true);
    
    try {
      await ApiService.logout();
    } catch (error) {
      // Continuer même en cas d'erreur
      // console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer l'état local
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      
      // Rediriger vers la page de connexion
      window.location.href = '/register';
    }
  };

  // Vérifier l'email
  const verifyEmail = async (token) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.verifyEmail(token);
      
      if (response.success && response.user) {
        // Mettre à jour l'utilisateur
        const updatedUser = { ...user, ...response.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        
        return { success: true };
      }
      
      return { success: false, message: response.message };
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Renvoyer l'email de vérification
  const resendVerificationEmail = async () => {
    if (!user?.email) {
      return { success: false, message: 'Email utilisateur non trouvé' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.resendVerificationEmail(user.email);
      return { success: response.success, message: response.message };
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour le profil utilisateur
  const updateUserProfile = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Valeur du contexte
  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    register,
    login,
    loginPhone,
    verifyOTP,
    logout,
    verifyEmail,
    resendVerificationEmail,
    updateUserProfile,
    checkAuthStatus,
    verifyPhoneNumber,
    verifyEmailAddress
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};